<template>
  <div
    v-if="isMobile && mobileNav.isDrawerOpen"
    class="sidebar__backdrop"
    @click="mobileNav.closeDrawer()"
  ></div>

  <div
    class="sidebar__wrapper"
    :class="{
      'sidebar__wrapper--full-collapsed': isFullCollapsed && !isMobile,
      'sidebar__wrapper--mobile': isMobile,
      'sidebar__wrapper--open': isMobile && mobileNav.isDrawerOpen,
    }"
    :aria-hidden="isMobile && !mobileNav.isDrawerOpen ? 'true' : undefined"
  >
    <div class="sidebar__container">
      <div
        class="subsidebar"
        :class="{ 'subsidebar--collapsed': isSubSidebarCollapsed }"
        v-if="store.activeModule && modules[store.activeModule]"
      >
        <div class="subsidebar__header">
          <div
            class="module-selector__button"
            @click="toggleModuleDropdown"
            :data-tooltip="isSubSidebarCollapsed ? 'module-selector' : ''"
          >
            <div class="module-selector__icon">
              <img
                v-if="store.activeModule === 'eplex'"
                src="/icons/eplex_logo_icon.png"
                class="module-selector__icon-img"
                alt="Eplex"
              />
              <i
                v-else
                :class="activeModuleIcon"
              ></i>
            </div>
            <span
              v-if="!isSubSidebarCollapsed"
              class="module-selector__name"
            >
              {{ activeModuleName }}
            </span>
            <span
              v-if="!isSubSidebarCollapsed"
              class="module-selector__arrow"
              :class="{ 'module-selector__arrow--rotated': moduleDropdownVisible }"
            >
              <i class="dx-icon-chevrondown"></i>
            </span>
          </div>

          <button
            v-if="!isMobile"
            type="button"
            class="toggle-subsidebar__btn toggle-subsidebar__btn--svg"
            :title="t('sidebar.toggleSubsidebar')"
            @click="toggleSubSidebar"
          >
            <ChevronToggleIcon :direction="isSubSidebarCollapsed ? 'right' : 'left'" />
          </button>

          <button
            v-else
            type="button"
            class="toggle-subsidebar__btn toggle-subsidebar__btn--svg"
            :aria-label="t('mobileNav.closeMenu')"
            @click="mobileNav.closeDrawer()"
          >
            <i class="dx-icon-close"></i>
          </button>
        </div>

        <div class="subsidebar__content">
          <div v-if="modules[store.activeModule].groups">
            <div
              v-for="group in modules[store.activeModule].groups"
              :key="group.title"
              class="submenu__group"
            >
              <div
                v-if="!isSubSidebarCollapsed"
                class="submenu__group-title"
              >
                {{ group.title }}
              </div>
              <ul class="submenu">
                <li
                  v-for="item in group.items"
                  :key="item.id"
                  class="submenu__item"
                  :class="{
                    'submenu__item--active': item.id === store.activeSubModule,
                    'submenu__item--disabled': isItemDisabled(item),
                  }"
                  :data-tooltip="`submodule-${item.id}`"
                >
                  <a
                    :href="`/app/${store.activeModule}/${item.id}`"
                    class="submenu__link"
                    @click.prevent="selectSubModule(item.id)"
                  >
                    <span class="submenu__icon">
                      <component
                        v-if="store.activeModule === 'eplex'"
                        :is="getEplexIcon(item.id)"
                      />
                      <component
                        v-if="store.activeModule === 'marketplace'"
                        :is="getMarketplaceIcon(item.id)"
                      />
                    </span>
                    <span
                      v-if="!isSubSidebarCollapsed"
                      class="submenu__title"
                    >
                      {{ item.name }}
                    </span>
                  </a>
                  <button
                    v-if="store.activeModule === 'eplex' && item.id === 'selection' && selectionActiveCount > 0"
                    type="button"
                    class="submenu__badge"
                    :title="t('selectionWidget.runningProcessesTitle')"
                    @click.stop.prevent="toggleProcessesPanel($event)"
                  >
                    {{ selectionActiveCount }}
                  </button>
                  <span
                    v-if="store.activeModule === 'eplex' && item.id === 'journal' && cartCount > 0"
                    class="submenu__badge submenu__badge--count"
                    :title="t('basket.itemsInCart', { n: cartCount })"
                  >
                    {{ cartCount }}
                  </span>
                  <Tooltip
                    v-if="isSubSidebarCollapsed"
                    :target="`[data-tooltip='submodule-${item.id}']`"
                    :text="item.name"
                  />
                </li>
              </ul>
            </div>
          </div>

          <div v-else>
            <ul class="submenu">
              <li
                v-for="section in modules[store.activeModule]?.subSections"
                :key="section.id"
                class="submenu__item"
                :class="{
                  'submenu__item--active': section.id === store.activeSubModule,
                }"
                :data-tooltip="`submodule-${section.id}`"
              >
                <a
                  :href="`/app/${store.activeModule}/${section.id}`"
                  class="submenu__link"
                  @click.prevent="selectSubModule(section.id)"
                >
                  <span class="submenu__icon">
                    <component
                      v-if="store.activeModule === 'eplex'"
                      :is="getEplexIcon(section.id)"
                    />
                  </span>
                  <span
                    v-if="!isSubSidebarCollapsed"
                    class="submenu__title"
                  >
                    {{ section.name }}
                  </span>
                </a>
                <span
                  v-if="store.activeModule === 'eplex' && section.id === 'journal' && cartCount > 0"
                  class="submenu__badge submenu__badge--count"
                  :title="t('basket.itemsInCart', { n: cartCount })"
                >
                  {{ cartCount }}
                </span>
                <Tooltip
                  v-if="isSubSidebarCollapsed"
                  :target="`[data-tooltip='submodule-${section.id}']`"
                  :text="section.name"
                />
              </li>
            </ul>
          </div>
        </div>

        <div class="subsidebar__sidebar-footer">
          <div
            v-if="isSignalWidgetMounted"
            class="sidebar-footer__button sidebar-footer__button--docs"
            :class="{ 'sidebar-footer__button--blocked': isGuidanceActive }"
            data-signal-docs
            :data-tooltip="isSubSidebarCollapsed ? 'docs-button' : ''"
          >
            <span class="sidebar-footer__link">
              <span class="sidebar-footer__icon">
                <DocsIcon />
              </span>
              <span
                v-if="!isSubSidebarCollapsed"
                class="sidebar-footer__label"
              >
                {{ t("main.documentation") }}
              </span>
            </span>
          </div>

          <div
            class="sidebar-footer__button sidebar-footer__button--support"
            :class="{
              'sidebar-footer__button--blocked': isGuidanceActive,
              'sidebar-footer__button--active': store.activeSubModule === 'support',
            }"
            :data-tooltip="isSubSidebarCollapsed ? 'support-button' : ''"
          >
            <a
              :href="`/app/${store.activeModule}/support/contact`"
              class="sidebar-footer__link"
              @click.prevent="openSupportContact"
            >
              <span class="sidebar-footer__icon">
                <GoSupportIcon />
              </span>
              <span
                v-if="!isSubSidebarCollapsed"
                class="sidebar-footer__label"
              >
                {{ t("main.support") }}
              </span>
            </a>
          </div>

          <div
            v-if="accountInfoStore.hasTokenBilling"
            class="sidebar-footer__button sidebar-footer__button--billing"
            :class="{
              'sidebar-footer__button--blocked': isGuidanceActive,
              'sidebar-footer__button--active': store.activeSubModule === 'billing',
            }"
            :data-tooltip="isSubSidebarCollapsed ? 'billing-button' : ''"
          >
            <a
              :href="`/app/${store.activeModule}/billing`"
              class="sidebar-footer__link"
              @click.prevent="openBilling"
            >
              <span class="sidebar-footer__icon">
                <BillingIcon />
              </span>
              <span
                v-if="!isSubSidebarCollapsed"
                class="sidebar-footer__label"
              >
                {{ t("billing.sidebarLabel") }}
              </span>
            </a>
          </div>

          <div
            class="sidebar-footer__button sidebar-footer__button--personalization"
            :class="{
              'sidebar-footer__button--blocked': isGuidanceActive,
              'sidebar-footer__button--active': store.activeSubModule === 'personalization',
            }"
            :data-tooltip="isSubSidebarCollapsed ? 'personalization-button' : ''"
          >
            <a
              :href="`/app/${store.activeModule}/personalization`"
              class="sidebar-footer__link"
              @click.prevent="openPersonalization"
            >
              <span class="sidebar-footer__icon">
                <PersonalizationIcon />
              </span>
              <span
                v-if="!isSubSidebarCollapsed"
                class="sidebar-footer__label"
              >
                {{ t("personalization.title") }}
              </span>
            </a>
          </div>

          <div
            v-if="pwaStore.canInstall"
            class="sidebar-footer__button sidebar-footer__button--install"
            :class="{ 'sidebar-footer__button--blocked': isGuidanceActive }"
            :data-tooltip="isSubSidebarCollapsed ? 'install-button' : ''"
            @click="install"
          >
            <span class="sidebar-footer__icon">
              <InstallIcon />
            </span>
            <span
              v-if="!isSubSidebarCollapsed"
              class="sidebar-footer__label"
            >
              {{ t("pwa.install") }}
            </span>
          </div>

          <div
            class="sidebar-footer__button sidebar-footer__button--logout"
            :class="{ 'sidebar-footer__button--blocked': isGuidanceActive }"
            @click="logout"
            :data-tooltip="isSubSidebarCollapsed ? 'logout-button' : ''"
          >
            <span class="sidebar-footer__icon">
              <LogoutIcon />
            </span>
            <span
              v-if="!isSubSidebarCollapsed"
              class="sidebar-footer__label"
            >
              {{ logoutLabel }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="moduleDropdownVisible"
        class="module-dropdown"
        :class="{ 'module-dropdown--from-collapsed': isSubSidebarCollapsed }"
        @click.stop
      >
        <div class="module-dropdown__header">
          <span class="module-dropdown__title">{{ t("sidebar.modules") }}</span>
          <button
            class="module-dropdown__close"
            @click="moduleDropdownVisible = false"
          >
            <i class="dx-icon-close"></i>
          </button>
        </div>

        <div class="module-dropdown__list">
          <div
            v-for="(module, key) in modules"
            :key="key"
            @click="handleModuleClick(key)"
            class="module-dropdown__item"
            :class="{
              'module-dropdown__item--active': key === store.activeModule,
              'module-dropdown__item--disabled': module.disabled,
            }"
          >
            <span class="module-dropdown__item-icon">
              <img
                v-if="key === 'eplex'"
                src="/icons/eplex_logo_icon.png"
                class="module-dropdown__item-icon-img"
                alt="Eplex"
              />
              <i
                v-else
                :class="module.icon"
              ></i>
            </span>
            <span class="module-dropdown__item-name">{{ module.name }}</span>
            <span
              v-if="key === store.activeModule"
              class="module-dropdown__check-mark"
            >
              <i class="dx-icon-check"></i>
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="moduleDropdownVisible"
        class="dropdown-overlay"
        @click="moduleDropdownVisible = false"
      ></div>
    </div>

    <div
      v-if="!isMobile"
      class="toggle-full-sidebar__button"
    >
      <button
        type="button"
        class="toggle-full-sidebar toggle-full-sidebar--svg"
        :title="t('sidebar.toggleSidebar')"
        @click="toggleFullSidebar"
      >
        <ChevronToggleIcon :direction="isFullCollapsed ? 'right' : 'left'" />
      </button>
    </div>
  </div>
  <Tooltip
    v-if="isSubSidebarCollapsed"
    :target="'[data-tooltip=module-selector]'"
    :text="activeModuleName"
  />
  <Tooltip
    v-if="isSubSidebarCollapsed && isSignalWidgetMounted"
    :target="'[data-tooltip=docs-button]'"
    :text="t('main.documentation')"
  />
  <Tooltip
    v-if="isSubSidebarCollapsed"
    :target="'[data-tooltip=support-button]'"
    :text="t('main.support')"
  />
  <Tooltip
    v-if="isSubSidebarCollapsed && accountInfoStore.hasTokenBilling"
    :target="'[data-tooltip=billing-button]'"
    :text="t('billing.sidebarLabel')"
  />
  <Tooltip
    v-if="isSubSidebarCollapsed"
    :target="'[data-tooltip=personalization-button]'"
    :text="t('personalization.title')"
  />
  <Tooltip
    v-if="isSubSidebarCollapsed && pwaStore.canInstall"
    :target="'[data-tooltip=install-button]'"
    :text="t('pwa.install')"
  />
  <Tooltip
    v-if="isSubSidebarCollapsed"
    :target="'[data-tooltip=logout-button]'"
    :text="logoutLabel"
  />
  <SelectionProcessesWidget
    v-if="processesPanelOpen"
    :anchor="processesAnchor"
    @close="processesPanelOpen = false"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, type Component } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useSidebarStore } from "../store/sidebar";
import { useMobileNavStore } from "../store/mobileNav";
import { usePwaStore } from "../store/pwa";
import { showInstallPrompt } from "../common/helpers/pwa/installPrompt";
import InstallIcon from "../assets/icons/InstallIcon.vue";
import { useBreakpoints } from "../common/composables/useBreakpoints";
import { useAuthStore } from "../store/authApi";
import { useEplexAccountInfoStore } from "../store/eplex/eplexAccountInfo";
import { useSelectionStore } from "../store/eplex/eplexSelection";
import { useQuickCartStore } from "../store/eplex/eplexQuickCart";
import SelectionProcessesWidget from "../modules/Eplex/components/groupPricing/SelectionProcessesWidget.vue";
import CartEplexIcon from "../assets/icons/eplex/CartEplexIcon.vue";
import FunctionSettingsIcon from "../assets/icons/eplex/FunctionSettingsIcon.vue";
import RetailPricingIcon from "../assets/icons/eplex/RetailPricingIcon.vue";
import SingleSearchIcon from "../assets/icons/eplex/SingleSearchIcon.vue";
import SelectionIcon from "../assets/icons/eplex/SelectionIcon.vue";
import Tooltip from "./Tooltip.vue";
import MyPriceIcon from "../assets/icons/marketplace/sidebarIcons/MyPriceIcon.vue";
import HypothesisIcon from "../assets/icons/marketplace/sidebarIcons/HypothesisIcon.vue";
import SummaryIcon from "../assets/icons/marketplace/sidebarIcons/SummaryIcon.vue";
import OrdersIcon from "../assets/icons/marketplace/sidebarIcons/OrdersIcon.vue";
import ReturnsIcon from "../assets/icons/marketplace/sidebarIcons/ReturnsIcon.vue";
import EventFeedIcon from "../assets/icons/marketplace/sidebarIcons/EventFeedIcon.vue";
import ReviewsIcon from "../assets/icons/marketplace/sidebarIcons/ReviewsIcon.vue";
import ReportsIcon from "../assets/icons/marketplace/sidebarIcons/ReportsIcon.vue";
import PriceSettingsIcon from "../assets/icons/marketplace/sidebarIcons/PriceSettingsIcon.vue";
import AutomationIcon from "../assets/icons/marketplace/sidebarIcons/AutomationIcon.vue";
import DeviationsIcon from "../assets/icons/marketplace/sidebarIcons/DeviationsIcon.vue";
import PlatformsIcon from "../assets/icons/marketplace/sidebarIcons/PlatformsIcon.vue";
import ZakooperSettingsIcon from "../assets/icons/marketplace/sidebarIcons/ZakooperSettingsIcon.vue";
import EmailAccountIcon from "../assets/icons/marketplace/sidebarIcons/EmailAccountIcon.vue";
import PivotSummaryIcon from "../assets/icons/marketplace/sidebarIcons/PivotSummaryIcon.vue";
import LicenseIcon from "../assets/icons/eplex/LicenseIcon.vue";
import GuidanceIcon from "../assets/icons/eplex/GuidanceIcon.vue";
import MarkupSettingsIcon from "../assets/icons/eplex/MarkupSettingsIcon.vue";
import Integration1CIcon from "../assets/icons/eplex/Integration1CIcon.vue";
import LogoutIcon from "../assets/icons/LogoutIcon.vue";
import GoSupportIcon from "../assets/icons/GoSupportIcon.vue";
import DocsIcon from "../assets/icons/DocsIcon.vue";
import PersonalizationIcon from "../assets/icons/PersonalizationIcon.vue";
import BillingIcon from "../assets/icons/BillingIcon.vue";
import ChevronToggleIcon from "../assets/icons/ChevronToggleIcon.vue";
import { useI18n } from "vue-i18n";
import { useGuidanceSidebar } from "../common/composables/useGuidanceSidebar";

const { t } = useI18n();

interface SubItem {
  id: string;
  name: string;
  disabled?: boolean;
}

interface SubGroup {
  title: string;
  items: SubItem[];
}

interface ModuleConfig {
  name: string;
  icon: string;
  disabled?: boolean;
  groups?: SubGroup[];
  subSections?: SubItem[];
}

const store = useSidebarStore();
const mobileNav = useMobileNavStore();
const pwaStore = usePwaStore();
const { isMobile } = useBreakpoints();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
// Раздел «Биллинг» — только клиентам на токеновом биллинге (CAB-733)
const accountInfoStore = useEplexAccountInfoStore();
const selectionStore = useSelectionStore();
const { isItemBlocked, isGuidanceActive } = useGuidanceSidebar();

const modules: Record<string, ModuleConfig> = store.modules;

const isSubSidebarCollapsedDesktop = ref(true);
// На телефоне панель выезжает поверх контента и места не экономит — иконочный
// рельс там только отнимает подписи, поэтому она всегда развёрнута.
const isSubSidebarCollapsed = computed({
  get: () => !isMobile.value && isSubSidebarCollapsedDesktop.value,
  set: (value: boolean) => (isSubSidebarCollapsedDesktop.value = value),
});
const isFullCollapsed = ref(false);
const moduleDropdownVisible = ref(false);
const isSignalWidgetMounted = ref(false);

const processesPanelOpen = ref(false);
const processesAnchor = ref<{ top: number; right: number }>({ top: 0, right: 0 });

const selectionActiveCount = computed(() => selectionStore.activeProcesses.length);

// счётчик позиций в корзине для бейджа на пункте «Корзина» — тот же источник
// (eplex/orders/journal?type=1), что использует Журнал заказов для своих позиций в корзине,
// чтобы бейдж не расходился с реальным содержимым корзины
const quickCartStore = useQuickCartStore();
const cartCount = computed(() => quickCartStore.count);
const refreshCartCount = () => {
  quickCartStore.load();
};

function toggleProcessesPanel(event: MouseEvent): void {
  if (processesPanelOpen.value) {
    processesPanelOpen.value = false;
    return;
  }
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  processesAnchor.value = { top: rect.top, right: rect.right };
  processesPanelOpen.value = true;
}

function ensureProcessesLoaded(): void {
  if (store.activeModule !== "eplex") return;
  // listLoaded (а не runs.length === 0): аккаунт без процессов имеет пустой runs
  // навсегда, поэтому старый guard перезапускал loadList на каждой навигации,
  // дублируя group_search/installed. Грузим один раз за сессию.
  if (!selectionStore.listLoaded && !selectionStore.isLoadingList) {
    selectionStore.loadList();
  }
}

const activeModuleName = computed(() => {
  return store.activeModule && modules[store.activeModule] ? modules[store.activeModule].name : t("sidebar.modules");
});

const activeModuleIcon = computed(() => {
  return store.activeModule && modules[store.activeModule] ? modules[store.activeModule].icon : "dx-icon-menu";
});

let signalObserver: MutationObserver | null = null;

function checkSignalWidget(): void {
  isSignalWidgetMounted.value = !!document.getElementById("signal-widget-root");
}

onMounted(() => {
  const pathSegments = route.path.split("/").filter(Boolean);
  if (pathSegments.length >= 2 && pathSegments[0] === "app") {
    const module = pathSegments[1];
    const subModule = pathSegments[2] || null;
    if (modules[module]) {
      store.setActiveModule(module);
      if (subModule) {
        store.setActiveSubModule(subModule);
      } else {
        const firstSubModule = getFirstSubModule(module);
        if (firstSubModule) {
          router.push(`/app/${module}/${firstSubModule}`);
        }
      }
    }
  }
  document.addEventListener("click", handleClickOutside);
  refreshCartCount();

  checkSignalWidget();
  signalObserver = new MutationObserver(checkSignalWidget);
  signalObserver.observe(document.body, { childList: true, subtree: true });

  ensureProcessesLoaded();
});

const selectModule = (module: string) => {
  const lastSub = store.lastActiveSubModules[module];
  const firstSubModule = getFirstSubModule(module);
  const targetSub = lastSub ?? firstSubModule;
  if (targetSub) {
    router.push(`/app/${module}/${targetSub}`);
  }
  moduleDropdownVisible.value = false;
  mobileNav.closeDrawer();
};

const selectSubModule = (subModule: string) => {
  if (!isItemDisabled({ id: subModule, name: "" })) {
    const pathSegments = route.path.split("/").filter(Boolean);
    const currentModule =
      store.activeModule ?? (pathSegments.length >= 2 && pathSegments[0] === "app" ? pathSegments[1] : null);

    if (currentModule && modules[currentModule]) {
      store.setActiveModule(currentModule);
      store.setActiveSubModule(subModule);
      router.push(`/app/${currentModule}/${subModule}`);
      mobileNav.closeDrawer();
    }
  }
};

const getFirstSubModule = (module: string): string | null => {
  const mod = store.modules[module];
  if (mod.groups && mod.groups.length) {
    return mod.groups[0].items[0]?.id || null;
  } else if (mod.subSections && mod.subSections.length) {
    return mod.subSections[0]?.id || null;
  }
  return null;
};

const handleModuleClick = (module: string) => {
  if (module === "bi") {
    window.open("https://bi.eplex.ru", "_blank", "noopener");
    moduleDropdownVisible.value = false;
  } else if (!modules[module].disabled) {
    selectModule(module);
  }
};

const isItemDisabled = (item: SubItem): boolean => item.disabled === true || isItemBlocked(item.id);

const toggleSubSidebar = () => {
  isSubSidebarCollapsed.value = !isSubSidebarCollapsed.value;
};

const toggleFullSidebar = () => {
  isFullCollapsed.value = !isFullCollapsed.value;
};

const toggleModuleDropdown = () => {
  moduleDropdownVisible.value = !moduleDropdownVisible.value;
};

const openSupportContact = () => {
  if (store.activeModule) {
    router.push(`/app/${store.activeModule}/support/contact`);
    mobileNav.closeDrawer();
  }
};

const openPersonalization = () => {
  if (store.activeModule) {
    store.setActiveSubModule("personalization");
    router.push(`/app/${store.activeModule}/personalization`);
    mobileNav.closeDrawer();
  }
};

const openBilling = () => {
  if (store.activeModule) {
    store.setActiveSubModule("billing");
    router.push(`/app/${store.activeModule}/billing`);
    mobileNav.closeDrawer();
  }
};

const handleClickOutside = (event: MouseEvent) => {
  const dropdown = document.querySelector(".module-dropdown");
  const button = document.querySelector(".module-selector__button");
  const sidebarWrapper = document.querySelector(".sidebar__wrapper");

  if (dropdown && button && !dropdown.contains(event.target as Node) && !button.contains(event.target as Node)) {
    moduleDropdownVisible.value = false;
  }

  const guidanceOverlay = document.querySelector(".guidance-overlay");
  const isGuidanceClick = guidanceOverlay?.contains(event.target as Node);
  if (
    !isMobile.value &&
    sidebarWrapper &&
    !sidebarWrapper.contains(event.target as Node) &&
    !isSubSidebarCollapsed.value &&
    !isGuidanceClick
  ) {
    isSubSidebarCollapsed.value = true;
  }
};

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  signalObserver?.disconnect();
});

/**
 * Кука единой авторизации живёт на домене .eplex.ru, поэтому выход закрывает сессию
 * и в СТО, и в биллинге. «Выйти из аккаунта» без уточнения обещало бы меньше, чем
 * происходит (CAB-641).
 */
const logoutLabel = computed(() => t("main.logOutUnified"));

/** Окно установки открывается только из жеста человека — отсюда и никак иначе. */
async function install(): Promise<void> {
  const accepted = await showInstallPrompt();
  if (accepted) pwaStore.canInstall = false;
  mobileNav.closeDrawer();
}

const logout = async () => {
  await authStore.logout();
  // сбрасываем процессы подбора, иначе каунтер прошлого аккаунта висит до перезагрузки
  selectionStore.reset();
  moduleDropdownVisible.value = false;
};

const eplexIcons: Record<string, Component> = {
  cart: CartEplexIcon,
  "function-settings": FunctionSettingsIcon,
  selection: SelectionIcon,
  journal: CartEplexIcon,
  "event-feed": EventFeedIcon,
  "retail-pricing": RetailPricingIcon,
  "single-search": SingleSearchIcon,
  "license-settings": LicenseIcon,
  "markup-settings": MarkupSettingsIcon,
  "integration-1c": Integration1CIcon,
  guidance: GuidanceIcon,
};

const marketplaceIcons: Record<string, Component> = {
  summary: SummaryIcon,
  "pivot-summary": PivotSummaryIcon,
  prices: MyPriceIcon,
  hypothesis: HypothesisIcon,
  orders: OrdersIcon,
  returns: ReturnsIcon,
  "event-feed": EventFeedIcon,
  reviews: ReviewsIcon,
  reports: ReportsIcon,
  "my-price-settings": PriceSettingsIcon,
  "automation-order-processes": AutomationIcon,
  "deviations-settings": DeviationsIcon,
  "platforms-settings": PlatformsIcon,
  "plugins-settings": FunctionSettingsIcon,
  "zakooper-settings": ZakooperSettingsIcon,
  "email-account": EmailAccountIcon,
};

const getEplexIcon = (id: string) => eplexIcons[id] || null;
const getMarketplaceIcon = (id: string) => marketplaceIcons[id] || null;

watch(
  () => route.path,
  (newPath) => {
    const pathSegments = newPath.split("/").filter(Boolean);

    if (pathSegments.length >= 2 && pathSegments[0] === "app") {
      const module = pathSegments[1];
      const subModule = pathSegments[2] || null;

      if (modules[module]) {
        if (store.activeModule !== module) {
          store.setActiveModule(module);
        }

        if (subModule && store.activeSubModule !== subModule) {
          store.setActiveSubModule(subModule);
        }

        if (module === "eplex") {
          ensureProcessesLoaded();
        }
      }
    }
  },
  { immediate: true },
);
</script>
